// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

// COB-500 — the "Flush browser stored key material" flow (COB-506 renamed
// it; it shipped as "Wipe this browser", and the internal vocabulary — the
// module names, the `wipe*` helpers, the step copy's verb — is still "wipe").
//
// Opened from the card at the foot of the Wallet page, and deliberately a
// different kind of control from "Lock session": Lock ends the session, this
// ends this browser's copy of the account. It is NOT account termination
// (`/account/abandon`) and NOT a whole-device wipe (COB-503) — the copy says
// so at every step, because the two nearby actions it could be mistaken for
// are both irreversible in ways this one is not.
//
// COB-506 also narrowed the flow's only UI entry point to SOFTWARE custody
// (`wipeCardVisible`, pages/wallet.tsx). The `web3auth` and `hardware` arms
// below are deliberately kept and still tested: nothing about them is wrong,
// they are simply not offered today, and re-exposing them is a one-line
// change to that predicate.
//
// Four gates, in order, and nothing is deleted until all four are passed:
//
//   1. **Passcode re-challenge** against the server (the house pattern from
//      the wallet's seed-export card). Software sessions only — see
//      `wipePasscodeChallengeApplies` for why a delegated/hardware session
//      cannot have one.
//   2. **What you stand to lose**, from data the SPA has already fetched:
//      held vaults, deposited BTC, live shared vaults, and the AR-21
//      zero-redundancy call-out for any `m == n` vault (policy and no-policy
//      variants say materially different things and are never merged). A
//      shared-vault member is pointed at COB-499's per-vault spec export —
//      the make-whole artifact — before, not after.
//   3. **Backup gate.** The COB-457 `KeyBackupCeremony`, reused verbatim:
//      passcode re-challenge → 24 words on the wallet card's 60s budget →
//      loss acknowledgement → `confirmBackup()`. Offered as a final reveal
//      even when the backup is already confirmed, and MANDATORY when it is
//      not. Agent-kind dispatch is `readKeyBackupStatus`'s, so `web3auth` is
//      never probed (the worker call rejects by design) and every failure
//      mode reads as unconfirmed.
//   4. **Type-it-back**, the abandon-flow pattern: retype the account email
//      plus an explicit irreversibility checkbox.
//
// The inventory fetch in step 2 is also load-bearing for step 4: the wipe
// may only touch vault IDs it can prove belong to this account, so a failed
// fetch BLOCKS the wipe rather than letting it run against a partial list.

import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { useSession } from '../session-context.js';
import { api, type SharedVaultSummary, type VaultSummary } from '../api.js';
import {
  KeyBackupCeremony,
  useKeyBackupGate,
} from './key-backup-gate.js';
import { SEED_LOSS_ACK_LABEL } from '../pages/onboarding.js';
import { revealFailureMessage } from '../pages/wallet.js';
import { formatBtc } from '../format.js';
import {
  executeBrowserWipe,
  localVaultIdsForFingerprint,
  resolveAccountVaultIds,
  summarizeWipeExposure,
  wipeAckLabel,
  wipeConfirmationMatches,
  wipePasscodeChallengeApplies,
  wipeSeedCeremonyApplies,
  wipeReadyToConfirm,
  type BrowserWipeResult,
} from '../browser-wipe.js';
import '../styles/browser-wipe.css';

type WipeStep = 'challenge' | 'exposure' | 'backup' | 'confirm' | 'wiping' | 'done';

interface Inventory {
  vaults: VaultSummary[];
  sharedVaults: SharedVaultSummary[];
}

// The control that opens this flow is `components/wipe-browser-card.tsx`,
// rendered at the foot of the Wallet page. It is a separate module on
// purpose: this one imports `revealFailureMessage` from `pages/wallet.tsx`,
// so a card exported from here would close an import cycle between the page
// and the component.

export function BrowserWipeModal({ onClose }: { onClose: () => void }) {
  const session = useSession();
  const { agent, agentKind, fingerprint, userEmail } = session;
  const backupGate = useKeyBackupGate();

  const challengeApplies = wipePasscodeChallengeApplies(agentKind);
  const [step, setStep] = useState<WipeStep>(
    challengeApplies ? 'challenge' : 'exposure',
  );
  const [passcode, setPasscode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [inventory, setInventory] = useState<Inventory | null>(null);
  const [inventoryError, setInventoryError] = useState<string | null>(null);
  const [inventoryNonce, setInventoryNonce] = useState(0);

  const [acknowledged, setAcknowledged] = useState(false);
  const [typedEmail, setTypedEmail] = useState('');
  const [irreversible, setIrreversible] = useState(false);
  const [result, setResult] = useState<BrowserWipeResult | null>(null);

  // Read-only, and fetched while the user is still on the challenge step so
  // the warning is instant when they reach it. A failure here is fatal to
  // the flow: the wipe scopes itself to these ids (see `resolveAccountVaultIds`).
  useEffect(() => {
    let cancelled = false;
    setInventory(null);
    setInventoryError(null);
    Promise.all([api.listVaults(), api.listSharedVaults()])
      .then(([vaultsResp, sharedVaults]) => {
        if (cancelled) return;
        setInventory({ vaults: vaultsResp.vaults, sharedVaults });
      })
      .catch(() => {
        if (cancelled) return;
        setInventoryError(
          'Could not read your vault list. This browser was NOT wiped — try again in a moment.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [inventoryNonce]);

  const exposure = useMemo(
    () =>
      inventory
        ? summarizeWipeExposure(inventory.vaults, inventory.sharedVaults)
        : null,
    [inventory],
  );

  const ackLabel = wipeAckLabel(agentKind, SEED_LOSS_ACK_LABEL);
  const backupSatisfied = wipeReadyToConfirm(
    agentKind,
    backupGate.satisfied,
    acknowledged,
  );
  const confirmMatches = wipeConfirmationMatches(typedEmail, userEmail);
  // Everything the LADDER requires, independent of which step is on screen —
  // the confirm button only renders on the confirm step, and `runWipe` adds
  // the step check itself as the handler-side guard.
  const canExecute =
    agent !== null &&
    agentKind !== null &&
    !!fingerprint &&
    inventory !== null &&
    backupSatisfied &&
    confirmMatches &&
    irreversible;

  const dismiss = useCallback(() => {
    if (step === 'wiping') return;
    onClose();
  }, [step, onClose]);

  async function verifyPasscode(e?: Event) {
    if (e) e.preventDefault();
    setError(null);
    if (!userEmail) {
      setError('Session email unavailable. Please sign out and back in.');
      return;
    }
    if (passcode.length === 0) {
      setError('Enter your passcode.');
      return;
    }
    setVerifying(true);
    try {
      const status = await session.passcodeCheck(userEmail, passcode);
      if (status !== 'match') {
        // Only `mismatch` has house copy that fits here. `no_account` /
        // `web3auth` mean this session's email does not resolve to a passcode
        // account at all — never silently render an empty error for them.
        setError(
          status === 'mismatch'
            ? revealFailureMessage('mismatch')
            : 'That passcode does not open this account. Sign out and back in, then try again.',
        );
        setPasscode('');
        return;
      }
      setPasscode('');
      setStep('exposure');
    } catch (err) {
      setError(revealFailureMessage('unknown', err));
      setPasscode('');
    } finally {
      setVerifying(false);
    }
  }

  async function runWipe() {
    // Handler-side guard behind the render gate — the same second line of
    // defence the shared-vault consent surfaces carry.
    if (
      step !== 'confirm' ||
      !canExecute ||
      !agent ||
      !agentKind ||
      !fingerprint ||
      !inventory
    ) {
      setError('This browser was not wiped — the confirmation is incomplete.');
      return;
    }
    setError(null);
    setStep('wiping');
    try {
      const vaultIds = resolveAccountVaultIds({
        vaults: inventory.vaults,
        sharedVaults: inventory.sharedVaults,
        localVaultIds: localVaultIdsForFingerprint(fingerprint),
      });
      const wipeResult = await executeBrowserWipe({
        agent,
        agentKind,
        fingerprint,
        vaultIds,
        // The Web3Auth SDK is a lazy chunk everywhere else in the SPA; keep
        // it out of the entry bundle here too by fetching it only on the
        // delegated path that actually needs it.
        web3authLogout: async () => {
          const mod = await import('../web3auth-login.js');
          await mod.web3authLogout();
        },
        logout: session.logout,
      });
      setResult(wipeResult);
      setStep('done');
    } catch (err) {
      setError(
        err instanceof Error
          ? `This browser was not fully wiped: ${err.message}`
          : 'This browser was not wiped. Please try again.',
      );
      setStep('confirm');
    }
  }

  return (
    <div
      class="wipe-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wipe-title"
      data-testid="browser-wipe-modal"
    >
      <div class="wipe-card">
        <div class="wipe-head">
          <h2 id="wipe-title" class="wipe-title">
            {step === 'done'
              ? 'Browser stored key material flushed'
              : 'Flush browser stored key material'}
          </h2>
          {step !== 'wiping' && step !== 'done' && (
            <button
              type="button"
              class="wipe-close"
              aria-label="Close"
              onClick={dismiss}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          )}
        </div>

        {/* COB-506: Richard's copy, verbatim, and pinned character-for-
            character in browser-wipe-modal.test.tsx. It is plain text on
            purpose — the account being flushed is named at gate 4, where the
            user types it back, so the lede no longer interpolates the email
            and no longer needs the emphasis that carried the scoping. */}
        {step !== 'done' && (
          <p class="wipe-lede">
            Remove this account's encrypted keys and local data from this
            browser. Local data is encrypted and specific to this browser but
            wiping data provides an extra level of protection for a browser
            and machine you rarely use or are switching from. Your account,
            your vaults and your Bitcoin are untouched, and you can sign back
            in here later by restoring the seed phrase you have backed up when
            you log in here next.
          </p>
        )}

        {error && (
          <p class="wipe-error" role="alert">
            {error}
          </p>
        )}

        {/* ── 1. Passcode re-challenge ─────────────────────────────── */}
        {step === 'challenge' && (
          <form class="wipe-step" onSubmit={verifyPasscode}>
            <p class="wipe-hint">
              Re-enter your passcode to continue. A signed-in session on its
              own is not enough to wipe a browser.
            </p>
            <label class="wipe-label" for="wipe-passcode">
              Passcode
            </label>
            <input
              id="wipe-passcode"
              class="wipe-input"
              type="password"
              autocomplete="current-password"
              value={passcode}
              disabled={verifying}
              onInput={(e) => setPasscode((e.target as HTMLInputElement).value)}
            />
            <div class="wipe-actions">
              <button type="button" class="btn btn-secondary" onClick={dismiss}>
                Cancel
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                disabled={verifying || passcode.length === 0}
              >
                {verifying ? 'Checking…' : 'Continue'}
              </button>
            </div>
          </form>
        )}

        {/* ── 2. What you stand to lose ────────────────────────────── */}
        {step === 'exposure' && (
          <div class="wipe-step" data-testid="wipe-exposure">
            {inventoryError && (
              <div class="wipe-warning">
                <p>{inventoryError}</p>
                <button
                  type="button"
                  class="btn btn-link"
                  onClick={() => setInventoryNonce((n) => n + 1)}
                >
                  Retry
                </button>
              </div>
            )}
            {!inventoryError && !exposure && (
              <p class="wipe-hint">Reading your vaults…</p>
            )}
            {exposure && (
              <>
                <ul class="wipe-facts">
                  <li>
                    {/* COB-637: both numbers describe the HELD set — the
                        count, and the BTC that has actually cleared into
                        those held vaults. The tile's `totalDepositedSats`
                        drops a reaccept-pending deposit and must never be
                        printed beside `heldCount` in this warning. */}
                    <strong>{exposure.totals.heldCount}</strong> strategy
                    vault{exposure.totals.heldCount === 1 ? '' : 's'} held,
                    holding <strong>{formatBtc(exposure.heldDepositedSats)}</strong>{' '}
                    BTC.
                  </li>
                  <li>
                    <strong>{exposure.liveShared.length}</strong> live shared
                    vault{exposure.liveShared.length === 1 ? '' : 's'} you
                    co-sign.
                  </li>
                </ul>
                <p class="wipe-hint">
                  Signing for these needs your key. After this wipe, this
                  browser can only sign again once you restore it here.
                </p>
                {exposure.zeroRedundancy.length > 0 && (
                  <div class="wipe-warning" data-testid="wipe-zero-redundancy">
                    <p class="wipe-warning-title">
                      Zero-redundancy shared vaults
                    </p>
                    <ul class="wipe-zero-list">
                      {exposure.zeroRedundancy.map((v) => (
                        <li key={v.vaultId}>
                          <strong>{v.name ?? v.vaultId.slice(0, 8)}</strong> —{' '}
                          {v.quorumM} of {v.n}. {v.warning}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {exposure.liveShared.length > 0 && (
                  <div class="wipe-spec-export">
                    <p class="wipe-hint">
                      Before wiping, download each shared vault's spec — it is
                      everything needed to watch or recreate the vault outside
                      Cobault, and it opens in a new tab so you don't lose your
                      place here.
                    </p>
                    <ul class="wipe-spec-list">
                      {exposure.liveShared.map((v) => (
                        <li key={v.vault_id}>
                          <a
                            href={`/shared-vault/${v.vault_id}`}
                            target="_blank"
                            rel="noopener"
                          >
                            Download vault spec — {v.name ?? v.vault_id.slice(0, 8)}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
            <div class="wipe-actions">
              <button type="button" class="btn btn-secondary" onClick={dismiss}>
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-primary"
                disabled={!exposure}
                onClick={() => setStep('backup')}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* ── 3. Backup gate ───────────────────────────────────────── */}
        {step === 'backup' && (
          <div class="wipe-step" data-testid="wipe-backup">
            {wipeSeedCeremonyApplies(agentKind) ? (
              <>
                <p class="wipe-hint">
                  {backupGate.satisfied
                    ? 'Your recovery phrase is the only way back into this account on this browser. Take a final look before it goes.'
                    : 'This browser has no confirmed backup of your recovery phrase. Back it up now — after the wipe it cannot be recovered from here.'}
                </p>
                <KeyBackupCeremony onConfirmed={backupGate.refresh} />
              </>
            ) : (
              <p class="wipe-hint">
                {agentKind === 'web3auth'
                  ? 'There is no recovery phrase to back up — your key comes from your sign-in provider. This wipe ends that session on this browser and clears your local vault data.'
                  : 'There is no recovery phrase to back up here — your key never leaves your hardware device. This wipe clears your local vault data and this browser’s record of your device wallet policies.'}
              </p>
            )}
            <label class="wipe-ack-row">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) =>
                  setAcknowledged((e.target as HTMLInputElement).checked)
                }
              />
              <span>{ackLabel}</span>
            </label>
            <div class="wipe-actions">
              <button
                type="button"
                class="btn btn-secondary"
                onClick={() => setStep('exposure')}
              >
                Back
              </button>
              <button
                type="button"
                class="btn btn-primary"
                disabled={!backupSatisfied}
                onClick={() => setStep('confirm')}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* ── 4. Type-it-back confirmation ─────────────────────────── */}
        {(step === 'confirm' || step === 'wiping') && (
          <div class="wipe-step" data-testid="wipe-confirm">
            <p class="wipe-hint">
              Type <strong>{userEmail ?? 'your account email'}</strong> to
              confirm which account this browser should forget.
            </p>
            <label class="wipe-label" for="wipe-email-confirm">
              Account email
            </label>
            <input
              id="wipe-email-confirm"
              class="wipe-input"
              type="email"
              autocomplete="off"
              value={typedEmail}
              disabled={step === 'wiping'}
              onInput={(e) => setTypedEmail((e.target as HTMLInputElement).value)}
            />
            <label class="wipe-ack-row">
              <input
                type="checkbox"
                checked={irreversible}
                disabled={step === 'wiping'}
                onChange={(e) =>
                  setIrreversible((e.target as HTMLInputElement).checked)
                }
              />
              <span>
                I understand this browser's copy of my keys and vault data is
                deleted immediately and cannot be undone from here.
              </span>
            </label>
            <div class="wipe-actions">
              <button
                type="button"
                class="btn btn-secondary"
                disabled={step === 'wiping'}
                onClick={() => setStep('backup')}
              >
                Back
              </button>
              <button
                type="button"
                class="btn btn-danger"
                disabled={!canExecute || step === 'wiping'}
                onClick={() => void runWipe()}
              >
                {step === 'wiping' ? 'Wiping…' : 'Flush key material'}
              </button>
            </div>
          </div>
        )}

        {/* ── Done ─────────────────────────────────────────────────── */}
        {step === 'done' && result && (
          <div class="wipe-step" data-testid="wipe-done">
            <p class="wipe-lede">
              Your keys and vault data for{' '}
              <strong>{userEmail ?? 'this account'}</strong> are gone from this
              browser, and you have been signed out. Your account and your
              Bitcoin are unaffected.
            </p>
            <ul class="wipe-facts">
              <li>
                {result.agentReport.deletedKeys.length} key record
                {result.agentReport.deletedKeys.length === 1 ? '' : 's'} removed
              </li>
              <li>
                {result.agentReport.clearedIndices.length} vault key mapping
                {result.agentReport.clearedIndices.length === 1 ? '' : 's'}{' '}
                removed
              </li>
              <li>
                {result.localReport.droppedVaultRows} local vault row
                {result.localReport.droppedVaultRows === 1 ? '' : 's'} and{' '}
                {result.localReport.removedKeys.length} stored setting
                {result.localReport.removedKeys.length === 1 ? '' : 's'} removed
              </li>
            </ul>
            <div class="wipe-actions">
              <button
                type="button"
                class="btn btn-primary"
                onClick={() => {
                  // Full navigation, not a route change: it drops every
                  // module-level singleton this page still holds (the
                  // Web3Auth SDK instance among them) so the reloaded SPA is
                  // genuinely indistinguishable from a fresh device.
                  if (typeof window !== 'undefined') window.location.assign('/');
                }}
              >
                Back to sign in
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
